import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, AlertTriangle, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { EmptyState, ErrorState, Mascot, SkeletonList } from "@/components/Mascot";

const Stat = ({ label, value, testid }) => (
  <div className="soft-card p-5 rise">
    <p className="text-xs text-neutral-400">{label}</p>
    <p className="font-display text-3xl font-semibold mt-1" data-testid={testid}>{value}</p>
  </div>
);

// Colour always comes with a text label, so levels never rely on colour alone.
const level = (m) => (m >= 75 ? { label: "Mastered", bar: "bg-emerald-400", text: "text-emerald-300" }
  : m >= 50 ? { label: "Getting there", bar: "bg-sky-400", text: "text-sky-300" }
  : { label: "Needs work", bar: "bg-yellow-300", text: "text-yellow-200" });

function TopicRow({ x, onPractice }) {
  const lv = level(x.mastery);
  return (
    <div data-testid={`progress-topic-${x.topic}`}>
      <div className="flex items-center justify-between gap-3 text-sm mb-1.5">
        <span className="truncate text-neutral-100" title={x.topic}>{x.topic}</span>
        <span className="flex items-center gap-3 shrink-0">
          <span className={`text-xs ${lv.text}`}>{lv.label} · {x.mastery}%</span>
          <button onClick={onPractice} className="text-xs text-sky-300 hover:text-sky-200 touch-target" data-testid={`progress-practice-${x.topic}`}>Practice</button>
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden"><div className={`h-full ${lv.bar} transition-[width] duration-500`} style={{ width: `${x.mastery}%` }} /></div>
      <p className="text-[11px] text-neutral-500 mt-1">{x.asked} asked · {x.practice_correct}/{x.practice_correct + x.practice_wrong} practice · {x.struggled + x.wrong} struggles</p>
    </div>
  );
}

function NextUp({ topic, onGo }) {
  if (!topic) return null;
  return (
    <div className="soft-card p-5 flex items-center gap-4 mt-6 border-sky-400/25" data-testid="progress-next-up">
      <Mascot pose="determined" className="h-14 w-14 shrink-0" decorative />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-sky-300">What to study next</p>
        <p className="font-display text-lg font-semibold truncate">{topic.topic}</p>
        <p className="text-xs text-neutral-400">{topic.subject} · {level(topic.mastery).label}</p>
      </div>
      <button onClick={onGo} className="h-11 px-4 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 inline-flex items-center gap-1.5 shrink-0" data-testid="progress-next-up-btn">
        Practice <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function Mistakes({ items }) {
  return (
    <div className="soft-card p-6" data-testid="progress-mistakes">
      <h3 className="font-display font-medium mb-5">Recurring mistakes & struggles</h3>
      {!items.length && <p className="text-sm text-neutral-400">None noticed yet. New mistakes show up here a few seconds after a chat or practice answer.</p>}
      <ul className="space-y-3">
        {items.map((m) => (
          <li key={m.entry_id} className="flex gap-2 text-sm text-neutral-200">
            {m.category === "mistake" ? <Flag className="h-4 w-4 text-red-300 shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-yellow-200 shrink-0 mt-0.5" />}
            {m.content}
          </li>
        ))}
      </ul>
    </div>
  );
}

const bySubject = (topics) => topics.reduce((acc, t) => ({ ...acc, [t.subject]: [...(acc[t.subject] || []), t] }), {});

// The progress experience, used on Home (and kept as its own page for old links).
export function ProgressSection() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => { setFailed(false); http.get("/progress").then((r) => setData(r.data)).catch(() => setFailed(true)); }, []);
  useEffect(load, [load]);
  if (failed) return <ErrorState text="Your progress didn't load." onRetry={load} testid="progress-error" />;
  if (!data) return <SkeletonList rows={3} testid="progress-loading" />;
  const t = data.totals;
  const acc = t.practice_answered ? Math.round((100 * t.practice_correct) / t.practice_answered) : 0;
  const groups = bySubject(data.topics);
  const next = [...data.topics].filter((x) => x.mastery < 75).sort((a, b) => a.mastery - b.mastery)[0];
  const practice = () => navigate("/app/practice");
  return (
    <div data-testid="progress-section">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat label="Topics studied" value={t.topics} testid="progress-topics" />
        <Stat label="Topics mastered" value={t.mastered} testid="progress-mastered" />
        <Stat label="Practice accuracy" value={t.practice_answered ? `${acc}%` : "—"} testid="progress-accuracy" />
        <Stat label="Chats" value={t.chats} testid="progress-chats" />
      </div>
      <NextUp topic={next} onGo={practice} />
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 mt-6">
        <div className="soft-card p-6" data-testid="progress-topic-list">
          <h3 className="font-display font-medium mb-5">Topics</h3>
          {!data.topics.length && <EmptyState testid="progress-empty" pose="thinking" title="No topics yet" text="Ask a few questions or practice, and your topics will show up here." action="Start a chat" to="/app/new" />}
          <div className="space-y-7 max-h-[420px] overflow-y-auto scroll-thin pr-1">
            {Object.entries(groups).map(([subject, list]) => (
              <section key={subject} data-testid={`progress-subject-${subject}`}>
                <p className="text-[11px] uppercase tracking-[0.16em] text-neutral-400 mb-3">{subject}</p>
                <div className="space-y-4">{list.map((x) => <TopicRow key={`${x.subject}-${x.topic}`} x={x} onPractice={practice} />)}</div>
              </section>
            ))}
          </div>
        </div>
        <Mistakes items={data.recurring_mistakes} />
      </div>
    </div>
  );
}
